import axios from 'axios'
import { v4 as makeUuid } from 'uuid'
import {
  IEmailLogApiClient,
  IEmailLogGetEmailDetailsRequest,
  IEmailLogGetEmailDetailsResponse,
  IEmailLogSearchParams,
  IEmailLogSearchResponse,
  IEmailLogAdvancedSearchRequest,
  IEmailLogAdvancedSearchResponse,
  IEmailLogDeliverEmailRequest,
  IEmailLogDeliverEmailResponse,
  IEmailLogMoveToJunkRequest,
  IEmailLogMoveToJunkResponse,
  IEmailLogDeleteEmailRequest,
  IEmailLogDeleteEmailResponse,
  IEmailLogTrackEventRequest,
  IEmailLogTrackEventResponse,
  IEmailLogMoveToInboxRequest,
  IEmailLogMoveToInboxResponse,
  IEmailLogDeleteAllRequest,
  IEmailLogDeleteAllResponse,
  IEmailLogCreateMultiAccountRemediationItemRequest,
  IEmailLogCreateMultiAccountRemediationItemResponse,
  IEmailLogGetMultiAccountRemediationItemRequest,
  IEmailLogGetMultiAccountRemediationItemResponse,
  IEmailLogStartMultiAccountRemediationRequest,
  IEmailLogStartMultiAccountRemediationResponse,
  IEmailLogGetBccUserSettingsRequest,
  IEmailLogGetBccUserSettingsResponse,
  IEmailLogDeleteMultiAccountRemediationItemRequest,
  IEmailLogDeleteMultiAccountRemediationItemResponse,
  IEmailLogGetAuthPoliciesRequest,
  IEmailLogGetAuthPoliciesResponse,
  IEmailLogStartSingleAccountRemediationRequest,
  IEmailLogStartSingleAccountRemediationResponse,
  IEmailLogGetAccountSettingsRequest,
  IEmailLogGetAccountSettingsResponse,
  IEmailLogPutAccountSettingsRequest,
  IEmailLogPatchAccountSettingsRequest,
  IEmailLogPutAccountSettingsResponse,
  IEmailLogPatchAccountSettingsResponse,
  IEmailLogGetRiskyConfigResponse,
  IEmailLogGetRiskyConfigRequest,
  IEmailLogGetMultiAccountsRiskyConfigRequest,
  IEmailLogGetMultiAccountsRiskyConfigResponse,
  IEmailLogPatchDomainSettingsRequest,
  IEmailLogPatchDomainSettingsResponse,
  IEmailLogPatchUserSettingsRequest,
  IEmailLogPatchUserSettingsResponse,
  IEmailLogIngestTelemetryRecordRequest,
  IEmailLogIngestTelemetryRecordResponse,
  IEmailLogGetUserListRequest,
  IEmailLogGetUserListResponse
} from '@barracuda-internal/converged-email-log'
import { IUrlUtils } from 'v2/modules/lib'

export const emailLogApi = axios.create()

export interface IEmailLogApiClientArgs {
  accessToken: string
  urlUtils: IUrlUtils
}

export interface IBccUserAccount {
  bcc_account_id: string
  bcc_account_name: string
  egd_account_id: string
  egd_account_region: string
}

export const EmailLogApiClient = ({
  accessToken,
  urlUtils
}: IEmailLogApiClientArgs): IEmailLogApiClient & {
  getBccUserAccounts: (
    abortController: AbortController
  ) => ({ bcc_user_id, with_services }: { bcc_user_id: string; with_services?: number[] }) => Promise<IBccUserAccount[]>
} => {
  emailLogApi.defaults.headers.common.Authorization = `Bearer ${accessToken}`

  const search =
    (abortController: AbortController) =>
    async (params: IEmailLogSearchParams): Promise<IEmailLogSearchResponse> => {
      const { bcc_account_id, ...queryParams } = params
      const res = await emailLogApi.get(`/external/v1/email-log/${urlUtils.urlEncode(bcc_account_id)}/search`, {
        signal: abortController.signal,
        params: queryParams,
        headers: {
          'X-Request-ID': makeUuid()
        }
      })
      return res.data
    }

  const advancedSearch =
    (abortController: AbortController) =>
    async (request: IEmailLogAdvancedSearchRequest): Promise<IEmailLogAdvancedSearchResponse> => {
      const { bcc_account_id, ...body } = request
      const res = await emailLogApi.post(
        `/external/v1/email-log/${urlUtils.urlEncode(bcc_account_id)}/advanced-search`,
        body,
        {
          signal: abortController.signal,
          headers: {
            'X-Request-ID': makeUuid()
          }
        }
      )
      return res.data
    }

  const getEmailDetails =
    (abortController: AbortController) =>
    async (request: IEmailLogGetEmailDetailsRequest): Promise<IEmailLogGetEmailDetailsResponse> => {
      const { bcc_account_id, message_id, origin } = request
      const res = await emailLogApi.get(
        `/external/v1/email-log/${urlUtils.urlEncode(bcc_account_id)}/email/${urlUtils.urlEncode(message_id)}`,
        {
          signal: abortController.signal,
          params: {
            origin
          },
          headers: {
            'X-Request-ID': makeUuid()
          }
        }
      )
      return res.data
    }

  const deliverEmail =
    (abortController: AbortController) =>
    async (request: IEmailLogDeliverEmailRequest): Promise<IEmailLogDeliverEmailResponse> => {
      const { bcc_account_id } = request.payload
      const res = await emailLogApi.post(
        `/external/v1/email-log/${urlUtils.urlEncode(bcc_account_id)}/email/deliver/pre-signed`,
        request,
        {
          signal: abortController.signal,
          headers: {
            'X-Request-ID': makeUuid()
          }
        }
      )
      return res.data
    }

  const moveToJunk =
    (abortController: AbortController) =>
    async (request: IEmailLogMoveToJunkRequest): Promise<IEmailLogMoveToJunkResponse> => {
      const { bcc_account_id } = request.payload
      const res = await emailLogApi.post(
        `/external/v1/email-log/${urlUtils.urlEncode(bcc_account_id)}/email/move-to-junk/pre-signed`,
        request,
        {
          signal: abortController.signal,
          headers: {
            'X-Request-ID': makeUuid()
          }
        }
      )
      return res.data
    }

  const deleteEmail =
    (abortController: AbortController) =>
    async (request: IEmailLogDeleteEmailRequest): Promise<IEmailLogDeleteEmailResponse> => {
      const { bcc_account_id } = request.payload
      const res = await emailLogApi.post(
        `/external/v1/email-log/${urlUtils.urlEncode(bcc_account_id)}/email/delete/pre-signed`,
        request,
        {
          signal: abortController.signal,
          headers: {
            'X-Request-ID': makeUuid()
          }
        }
      )
      return res.data
    }

  const moveToInbox =
    (abortController: AbortController) =>
    async (request: IEmailLogMoveToInboxRequest): Promise<IEmailLogMoveToInboxResponse> => {
      const { bcc_account_id } = request.payload
      const res = await emailLogApi.post(
        `/external/v1/email-log/${urlUtils.urlEncode(bcc_account_id)}/email/move-to-inbox/pre-signed`,
        request,
        {
          signal: abortController.signal,
          headers: {
            'X-Request-ID': makeUuid()
          }
        }
      )
      return res.data
    }

  const trackMixpanelEvent = async (request: IEmailLogTrackEventRequest): Promise<IEmailLogTrackEventResponse> => {
    const { bcc_account_id, ...data } = request
    const res = await emailLogApi.post(
      `/external/v1/email-log/${urlUtils.urlEncode(bcc_account_id)}/track-event`,
      data,
      {
        headers: {
          'X-Request-ID': makeUuid()
        }
      }
    )
    return res.data
  }

  const deleteAll =
    (abortController: AbortController) =>
    async (request: IEmailLogDeleteAllRequest): Promise<IEmailLogDeleteAllResponse> => {
      const { bcc_account_id } = request
      const res = await emailLogApi.post(
        `/external/v1/email-log/${urlUtils.urlEncode(bcc_account_id)}/bulk-remediate`,
        request,
        {
          signal: abortController.signal,
          headers: {
            'X-Request-ID': makeUuid()
          }
        }
      )
      return res.data
    }

  const createMultiAccountRemediationItem =
    (abortController: AbortController) =>
    async (
      request: IEmailLogCreateMultiAccountRemediationItemRequest
    ): Promise<IEmailLogCreateMultiAccountRemediationItemResponse> => {
      const { bcc_user_id, ...body } = request
      const res = await emailLogApi.post('/v2/email-protection-service/multi-account-remediation', body, {
        signal: abortController.signal,
        headers: {
          'X-Request-ID': makeUuid()
        },
        params: {
          bcc_user_id
        }
      })
      return res.data
    }

  const getMultiAccountRemediationItem =
    (abortController: AbortController) =>
    async (
      request: IEmailLogGetMultiAccountRemediationItemRequest
    ): Promise<IEmailLogGetMultiAccountRemediationItemResponse> => {
      const { bcc_user_id, item_id } = request
      const res = await emailLogApi.get(
        `/v2/email-protection-service/multi-account-remediation/${urlUtils.urlEncode(item_id)}`,
        {
          signal: abortController.signal,
          headers: {
            'X-Request-ID': makeUuid()
          },
          params: {
            bcc_user_id
          }
        }
      )
      return res.data
    }

  const startMultiAccountRemediation =
    (abortController: AbortController) =>
    async (
      request: IEmailLogStartMultiAccountRemediationRequest
    ): Promise<IEmailLogStartMultiAccountRemediationResponse> => {
      const { bcc_user_id, item_id, bcc_account_ids } = request
      const res = await emailLogApi.post(
        `/v2/email-protection-service/multi-account-remediation/${urlUtils.urlEncode(item_id)}/remediate`,
        {
          bcc_account_ids
        },
        {
          signal: abortController.signal,
          headers: {
            'X-Request-ID': makeUuid()
          },
          params: {
            bcc_user_id
          }
        }
      )
      return res.data
    }

  const deleteMultiAccountRemediationItem =
    (abortController: AbortController) =>
    async (
      request: IEmailLogDeleteMultiAccountRemediationItemRequest
    ): Promise<IEmailLogDeleteMultiAccountRemediationItemResponse> => {
      const { bcc_user_id, item_id } = request
      const res = await emailLogApi.delete(
        `/v2/email-protection-service/multi-account-remediation/${urlUtils.urlEncode(item_id)}`,
        {
          signal: abortController.signal,
          headers: {
            'X-Request-ID': makeUuid()
          },
          params: {
            bcc_user_id
          }
        }
      )
      return res.data
    }

  const getBccUserSettings =
    (abortController: AbortController) =>
    async (request: IEmailLogGetBccUserSettingsRequest): Promise<IEmailLogGetBccUserSettingsResponse> => {
      const { bcc_user_id, variables } = request
      const res = await emailLogApi.get(`/v2/bcc-user-service/bcc-user/${urlUtils.urlEncode(bcc_user_id)}/settings`, {
        signal: abortController.signal,
        headers: {
          'X-Request-ID': makeUuid()
        },
        params: {
          variables: variables.join(',')
        }
      })
      return res.data
    }

  const getBccUserAccounts =
    (abortController: AbortController) =>
    async ({
      bcc_user_id,
      with_services
    }: {
      bcc_user_id: string
      with_services?: number[]
    }): Promise<IBccUserAccount[]> => {
      const res = await emailLogApi.get(`/v2/bcc-user-service/bcc-user/${urlUtils.urlEncode(bcc_user_id)}/accounts`, {
        signal: abortController.signal,
        headers: {
          'X-Request-ID': makeUuid()
        },
        params: {
          with_services: with_services ? with_services.join(',') : undefined
        }
      })
      return res.data
    }

  const getAvailableAuthPolicies =
    (abortController: AbortController) =>
    async (request: IEmailLogGetAuthPoliciesRequest): Promise<IEmailLogGetAuthPoliciesResponse> => {
      const { subject } = request
      const res = await emailLogApi.get('/v2/auth-service/me/auth-policies', {
        signal: abortController.signal,
        params: { subject: subject?.join(',') },
        headers: {
          'X-Request-ID': makeUuid()
        }
      })

      return res.data
    }

  const startSingleAccountRemediation =
    (abortController: AbortController) =>
    async (
      request: IEmailLogStartSingleAccountRemediationRequest
    ): Promise<IEmailLogStartSingleAccountRemediationResponse> => {
      const { bcc_account_id, ...data } = request
      const res = await emailLogApi.post(
        `/v2/email-protection-service/bcc-account/${urlUtils.urlEncode(bcc_account_id)}/remediation`,
        data,
        {
          signal: abortController.signal,
          headers: { 'X-Request-ID': makeUuid() }
        }
      )

      return res.data
    }

  const getAccountSettings =
    (abortController: AbortController) =>
    async (request: IEmailLogGetAccountSettingsRequest): Promise<IEmailLogGetAccountSettingsResponse> => {
      const { account_id, variables } = request
      const res = await emailLogApi.get(`/v2/account-service/account/${urlUtils.urlEncode(account_id)}/settings`, {
        signal: abortController.signal,
        headers: {
          'X-Request-ID': makeUuid()
        },
        params: {
          variables: variables.join(',')
        }
      })
      return res.data
    }

  const putAccountSettings =
    (abortController: AbortController) =>
    async (request: IEmailLogPutAccountSettingsRequest): Promise<IEmailLogPutAccountSettingsResponse> => {
      const { account_id, settings } = request
      const res = await emailLogApi.put(
        `/v2/account-service/account/${urlUtils.urlEncode(account_id)}/settings`,
        settings,
        {
          signal: abortController.signal,
          headers: {
            'X-Request-ID': makeUuid()
          }
        }
      )
      return res.data
    }

  const patchAccountSettings =
    (abortController: AbortController) =>
    async (request: IEmailLogPatchAccountSettingsRequest): Promise<IEmailLogPatchAccountSettingsResponse> => {
      const { account_id, variable, operation, payload } = request
      const res = await emailLogApi.patch(
        `/v2/account-service/account/${urlUtils.urlEncode(account_id)}/setting/${urlUtils.urlEncode(variable)}`,
        { variable, operation, payload },
        {
          signal: abortController.signal,
          headers: {
            'X-Request-ID': makeUuid()
          }
        }
      )
      return res.data
    }

  const getRiskyConfig =
    (abortController: AbortController) =>
    async (request: IEmailLogGetRiskyConfigRequest): Promise<IEmailLogGetRiskyConfigResponse> => {
      const { account_id } = request
      const res = await emailLogApi.get(
        `/v2/email-protection-service/egd-account/${urlUtils.urlEncode(account_id)}/risky-configuration`,
        {
          signal: abortController.signal,
          headers: {
            'X-Request-ID': makeUuid()
          }
        }
      )
      return res.data
    }

  const getMultiAccountsRiskyConfig =
    (abortController: AbortController) =>
    async (
      request: IEmailLogGetMultiAccountsRiskyConfigRequest
    ): Promise<IEmailLogGetMultiAccountsRiskyConfigResponse> => {
      const res = await emailLogApi.get('/v2/email-protection-service/risky-configuration/all-accounts', {
        signal: abortController.signal,
        headers: {
          'X-Request-ID': makeUuid()
        }
      })
      return res.data
    }

  const patchDomainSettings =
    (abortController: AbortController) =>
    async (request: IEmailLogPatchDomainSettingsRequest): Promise<IEmailLogPatchDomainSettingsResponse> => {
      const { domain_id, variable, operation, payload } = request
      const res = await emailLogApi.patch(
        `/v2/domain-service/domain/${urlUtils.urlEncode(domain_id)}/setting/${urlUtils.urlEncode(variable)}`,
        { variable, operation, payload },
        {
          signal: abortController.signal,
          headers: {
            'X-Request-ID': makeUuid()
          }
        }
      )
      return res.data
    }

  const patchUserSettings =
    (abortController: AbortController) =>
    async (request: IEmailLogPatchUserSettingsRequest): Promise<IEmailLogPatchUserSettingsResponse> => {
      const { account_id, user_id, variable, operation, payload } = request
      const res = await emailLogApi.patch(
        `/v2/user-service/account/${urlUtils.urlEncode(account_id)}/user/${urlUtils.urlEncode(
          user_id
        )}/setting/${urlUtils.urlEncode(variable)}`,
        { variable, operation, payload },
        {
          signal: abortController.signal,
          headers: {
            'X-Request-ID': makeUuid()
          }
        }
      )
      return res.data
    }

  const ingestTelemetryRecord =
    (abortController: AbortController) =>
    async (request: IEmailLogIngestTelemetryRecordRequest): Promise<IEmailLogIngestTelemetryRecordResponse> => {
      const { provider, record } = request
      const res = await emailLogApi.post(
        '/v2/telemetry-service/event/ingest',
        { provider, record },
        {
          signal: abortController.signal,
          headers: {
            'X-Request-ID': makeUuid()
          }
        }
      )
      return res.data
    }

  const getUserList =
    (abortController: AbortController) =>
    async (request: IEmailLogGetUserListRequest): Promise<IEmailLogGetUserListResponse> => {
      const { account_id } = request
      const res = await emailLogApi.get(`/v2/account-service/account/${urlUtils.urlEncode(account_id)}/user-ids`, {
        signal: abortController.signal,
        headers: {
          'X-Request-ID': makeUuid()
        }
      })
      return res.data
    }

  return Object.freeze({
    advancedSearch,
    createMultiAccountRemediationItem,
    deleteAll,
    deleteEmail,
    deleteMultiAccountRemediationItem,
    deliverEmail,
    getAccountSettings,
    putAccountSettings,
    patchAccountSettings,
    getAvailableAuthPolicies,
    getBccUserAccounts,
    getBccUserSettings,
    getEmailDetails,
    getMultiAccountRemediationItem,
    moveToInbox,
    moveToJunk,
    search,
    startMultiAccountRemediation,
    startSingleAccountRemediation,
    trackMixpanelEvent,
    getRiskyConfig,
    getMultiAccountsRiskyConfig,
    patchDomainSettings,
    patchUserSettings,
    ingestTelemetryRecord,
    getUserList
  })
}
